"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronDown, Trash2, Upload, X } from "lucide-react";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { HK_DIAL_CODE, phoneSchemaFor } from "@/lib/validation/auth";
import { CountryCodeSelect } from "@/components/auth/country-code-select";
import { FieldError } from "@/components/auth/field-error";

// Mirrors the option lists shown on the /auth/onboarding occupation step so the
// account profile edit-modal stays consistent with what the user picked at
// signup.
const INDUSTRY_OPTIONS = ["金融", "科技", "地產", "教育", "醫療", "其他"];
const POSITION_OPTIONS = [
  "高級管理",
  "中層管理",
  "基層員工",
  "自僱人士",
  "學生",
  "其他",
];
const SENIORITY_OPTIONS = [
  "0-2 年",
  "3-5 年",
  "6-10 年",
  "11-20 年",
  "20 年以上",
];

export type ProfileValues = {
  firstName: string;
  lastName: string;
  phone: string;
  industry: string;
  position: string;
  rank: string;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues: ProfileValues;
  initialAvatar: string | null;
  onSave: (next: ProfileValues, avatar: string | null) => void;
}

export function EditProfileModal({
  open,
  onOpenChange,
  initialValues,
  initialAvatar,
  onSave,
}: Props) {
  const [draft, setDraft] = React.useState(initialValues);
  const [avatar, setAvatar] = React.useState<string | null>(initialAvatar);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(initialValues);
      setAvatar(initialAvatar);
    }
  }, [open, initialValues, initialAvatar]);

  const update =
    (key: keyof ProfileValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft((prev) => ({ ...prev, [key]: e.target.value }));

  const [dialCode, setDialCode] = React.useState(HK_DIAL_CODE);

  /**
   * Digits only, capped at the length the dial code expects (8 for +852).
   * Typing a letter or a 9th digit simply does not register, rather than
   * being accepted and rejected later by the schema.
   */
  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const max = dialCode === HK_DIAL_CODE ? 8 : 15;
    const digits = e.target.value.replace(/\D/g, "").slice(0, max);
    setDraft((prev) => ({ ...prev, phone: digits }));
  };
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const handleAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAvatar(reader.result as string);
    reader.readAsDataURL(file);
  };

  /**
   * Validate before saving. This form previously called `onSave` with whatever
   * was typed — QA: "電話應該要容許選取區號同埋輸入電話號碼， 如果係香港電話
   * 號碼需要檢查係咪八個字". Uses the same schemas as the auth pages.
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (draft.phone.trim()) {
      const r = phoneSchemaFor(dialCode).safeParse(draft.phone);
      if (!r.success) next.phone = r.error.issues[0]?.message ?? "電話號碼格式不正確";
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSave(draft, avatar);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-48px)] w-[calc(100vw-32px)] max-w-[520px] -translate-x-1/2 -translate-y-1/2 flex-col bg-white shadow-xl"
          style={{ borderRadius: "3px" }}
        >
          <DialogPrimitive.Title
            className="px-[32px] pt-[32px]"
            style={{
              fontFamily: CORE_FONT.condensed,
              fontSize: "24px",
              fontWeight: 700,
              color: CORE_COLORS.textPrimary,
              lineHeight: 1.1,
              letterSpacing: "-0.2px",
            }}
          >
            修改個人資料
          </DialogPrimitive.Title>

          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex flex-col gap-[16px] overflow-y-auto px-[32px] py-[24px]">
              {/* Avatar — combined into the same modal */}
              <div className="flex items-center gap-[16px]">
                <div
                  className="relative shrink-0 overflow-hidden rounded-full"
                  style={{
                    width: 80,
                    height: 80,
                    backgroundColor: CORE_COLORS.dividerLight,
                    border: `1px solid ${CORE_COLORS.dividerSoft}`,
                  }}
                >
                  {avatar ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={avatar}
                      alt="頭像預覽"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{
                        fontFamily: CORE_FONT.sans,
                        fontSize: 26,
                        fontWeight: 700,
                        color: CORE_COLORS.textPrimary,
                      }}
                    >
                      AC
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-[8px]">
                  <span
                    style={{
                      fontFamily: CORE_FONT.sans,
                      fontSize: 13,
                      fontWeight: 700,
                      color: CORE_COLORS.textPrimary,
                      letterSpacing: "0.2px",
                    }}
                  >
                    頭像
                  </span>
                  <div className="flex flex-wrap items-center gap-[8px]">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarPick}
                    />
                    <button
                      type="button"
                      aria-label="上傳頭像"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-[6px] hover:bg-[color:var(--green)]/[0.05]"
                      style={
                        {
                          fontFamily: CORE_FONT.sans,
                          fontSize: 13,
                          fontWeight: 700,
                          color: CORE_COLORS.brandGreen,
                          lineHeight: "18px",
                          padding: "6px 12px",
                          border: `1px solid ${CORE_COLORS.brandGreen}`,
                          borderRadius: 3,
                          ["--green" as string]: CORE_COLORS.brandGreen,
                        } as React.CSSProperties
                      }
                    >
                      <Upload className="size-[14px]" strokeWidth={2.4} />
                      <span>{avatar ? "更換相片" : "上傳相片"}</span>
                    </button>
                    {avatar && (
                      <button
                        type="button"
                        aria-label="移除頭像"
                        onClick={() => setAvatar(null)}
                        className="inline-flex items-center gap-[6px] hover:bg-black/[0.04]"
                        style={{
                          fontFamily: CORE_FONT.sans,
                          fontSize: 13,
                          fontWeight: 700,
                          color: CORE_COLORS.textSecondary,
                          lineHeight: "18px",
                          padding: "6px 12px",
                          border: `1px solid ${CORE_COLORS.dividerSoft}`,
                          borderRadius: 3,
                        }}
                      >
                        <Trash2 className="size-[14px]" strokeWidth={2.4} />
                        <span>移除</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div
                aria-hidden
                style={{ borderTop: `1px solid ${CORE_COLORS.dividerSoft}` }}
              />

              <div className="grid grid-cols-2 gap-[16px]">
                <TextField
                  id="v2-edit-firstname"
                  label="名字"
                  value={draft.firstName}
                  onChange={update("firstName")}
                  autoComplete="given-name"
                />
                <TextField
                  id="v2-edit-lastname"
                  label="姓氏"
                  value={draft.lastName}
                  onChange={update("lastName")}
                  autoComplete="family-name"
                />
              </div>
              {/* Country code + number, with HK validated as 8 digits. */}
              <div className="flex flex-col gap-[6px]">
                <label
                  htmlFor="v2-edit-phone"
                  style={{
                    fontFamily: CORE_FONT.sans,
                    fontSize: 13,
                    fontWeight: 700,
                    color: CORE_COLORS.textPrimary,
                  }}
                >
                  電話
                </label>
                <div className="flex items-start gap-[8px]">
                  {/* 44 to match TextField's input beside it — the shared
                      control defaults to the 48px the auth forms use. */}
                  <CountryCodeSelect
                    id="v2-edit-dial"
                    value={dialCode}
                    onChange={setDialCode}
                    height={44}
                  />
                  <div className="min-w-0 flex-1">
                    {/* A Hong Kong number is exactly 8 digits, which the
                        schema already enforces on submit — the field itself
                        accepted any length, so the only feedback was an error
                        after the fact. Capped here (and non-digits dropped as
                        you type) so an invalid number cannot be entered.
                        QA: "change phone number to 8 digit number". */}
                    <TextField
                      id="v2-edit-phone"
                      label=""
                      value={draft.phone}
                      onChange={onPhoneChange}
                      autoComplete="tel"
                      inputMode="tel"
                      maxLength={dialCode === HK_DIAL_CODE ? 8 : 15}
                    />
                  </div>
                </div>
                <FieldError id="v2-edit-phone" message={errors.phone} />
              </div>
              <SelectField
                id="v2-edit-industry"
                label="行業"
                value={draft.industry}
                placeholder="請選擇行業"
                options={INDUSTRY_OPTIONS}
                onChange={(v) =>
                  setDraft((prev) => ({ ...prev, industry: v }))
                }
              />
              <div className="grid grid-cols-2 gap-[16px]">
                <SelectField
                  id="v2-edit-position"
                  label="職位"
                  value={draft.position}
                  placeholder="請選擇職位"
                  options={POSITION_OPTIONS}
                  onChange={(v) =>
                    setDraft((prev) => ({ ...prev, position: v }))
                  }
                />
                <SelectField
                  id="v2-edit-rank"
                  label="職級"
                  value={draft.rank}
                  placeholder="請選擇職級"
                  options={SENIORITY_OPTIONS}
                  onChange={(v) =>
                    setDraft((prev) => ({ ...prev, rank: v }))
                  }
                />
              </div>
            </div>

            <div className="flex items-center gap-[8px] px-[32px] pb-[32px]">
              <button
                type="submit"
                className="inline-flex flex-1 items-center justify-center transition-colors hover:bg-[color:var(--green-dark)]"
                style={
                  {
                    fontFamily: CORE_FONT.sans,
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "white",
                    lineHeight: "20px",
                    padding: "12px 20px",
                    backgroundColor: CORE_COLORS.brandGreen,
                    borderRadius: "3px",
                    ["--green-dark" as string]: CORE_COLORS.brandGreenDark,
                  } as React.CSSProperties
                }
              >
                儲存
              </button>
            </div>
          </form>

          <DialogPrimitive.Close
            aria-label="關閉"
            className="absolute right-[16px] top-[16px] inline-flex size-[28px] items-center justify-center transition-colors hover:bg-black/[0.05]"
            style={{
              border: `1px solid rgba(0,0,0,0.15)`,
              borderRadius: "999px",
            }}
          >
            <X
              className="size-[14px]"
              strokeWidth={2}
              color={CORE_COLORS.textPrimary}
            />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  inputMode,
  maxLength,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: "text" | "tel" | "email";
  maxLength?: number;
}) {
  return (
    <div className="flex w-full flex-col gap-[6px]">
      {/* Only when there is one. The phone row passes `label=""` because its
          label is rendered by the row itself, and an empty <label> still took
          a full 18px line plus the 6px gap — which is why the input sat 6px
          below the dial-code select beside it. */}
      {label && (
        <label
          htmlFor={id}
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: "13px",
            fontWeight: 700,
            color: CORE_COLORS.textPrimary,
            lineHeight: "18px",
            letterSpacing: "0.2px",
          }}
        >
          {label}
        </label>
      )}
      <input
        id={id}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        className="bg-white outline-none focus-visible:border-[color:var(--green)]"
        style={
          {
            fontFamily: CORE_FONT.sans,
            fontSize: "15px",
            color: CORE_COLORS.textPrimary,
            lineHeight: "22px",
            padding: "10px 14px",
            height: "44px",
            border: `1px solid rgba(0,0,0,0.3)`,
            borderRadius: "3px",
            ["--green" as string]: CORE_COLORS.brandGreen,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

function SelectField({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex w-full flex-col gap-[6px]">
      <label
        htmlFor={id}
        style={{
          fontFamily: CORE_FONT.sans,
          fontSize: "13px",
          fontWeight: 700,
          color: CORE_COLORS.textPrimary,
          lineHeight: "18px",
          letterSpacing: "0.2px",
        }}
      >
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-white outline-none focus-visible:border-[color:var(--green)] appearance-none"
          style={
            {
              fontFamily: CORE_FONT.sans,
              fontSize: "15px",
              color: value ? CORE_COLORS.textPrimary : "rgba(0,0,0,0.4)",
              lineHeight: "22px",
              padding: "10px 36px 10px 14px",
              height: "44px",
              border: `1px solid rgba(0,0,0,0.3)`,
              borderRadius: "3px",
              width: "100%",
              ["--green" as string]: CORE_COLORS.brandGreen,
            } as React.CSSProperties
          }
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 size-[16px]"
          color={CORE_COLORS.textSecondary}
          strokeWidth={2}
        />
      </div>
    </div>
  );
}
